import { Input } from '@nike/frame-component-library';
import PropTypes from 'prop-types';
import React from 'react';

import {
  trackScsKeyEditStarted,
  trackScsKeyScopeChanged,
  trackScsScopeDiffCheckTriggered,
  trackScsScopeDiffCheckCancelled,
} from '../../utils/analytics';
import { formatStoreAlias } from '../../utils/formatting';
import {
  scsReportGet, scsScheduleGet
} from '../../utils/service-calls/scs';
import { toGeo, getCountriesByGeo } from '../../utils/static/scs-geo-iso-map';
import { REQUIRED_FIELD } from '../../utils/validation/input-validation';
import {
  ButtonBlack,
  ButtonWhite,
  Select,
  TableRowClick,
} from '../reusable';
import Modal from '../reusable/CommonModal/Modal';

import * as styles from './Keyset.styles';
import ScheduleForLater, { ScheduleBanner } from './ScheduleForLater';

const GEO = 'GEO';
const COUNTRY = 'COUNTRY';
const GLOBAL = 'GLOBAL';
const STORE = 'STORE';

// Modal steps
const STEP_EDIT = 'edit';
const STEP_DIFF_CHECK = 'diffCheck';
const STEP_CONFIRM = 'confirm';

const getHigherScopeTypes = (scopeType) => {
  const scopeHierarchyArr = [STORE, COUNTRY, GEO, GLOBAL];
  const index = scopeHierarchyArr.indexOf(scopeType);
  const preceding = scopeHierarchyArr.slice(0, index);
  return preceding;
};

const filterStoresWithDiffVal = (value, scopeType, countries, report, storeList) => {
  const isStoreInCountry = (store) => {
    if (countries.length === 0) {
      return true;
    }
    return countries.filter((country) => store.startsWith(country)).length > 0;
  };
  const storeNameMap = new Map(
    storeList.map((store) => [
      `${store.address.country}-${store.storeNumber}`,
      store.name,
    ])
  );
  const storeGeoMap = new Map(
    storeList.map((store) => [
      `${store.address.country}-${store.storeNumber}`,
      toGeo(store),
    ])
  );
  const higherScopes = getHigherScopeTypes(scopeType);
  const result = report
    .filter((item) => String(item.value) !== String(value))
    .filter((item) => higherScopes.includes(item.scopeType))
    .flatMap((item) => item.stores
        .filter((store) => isStoreInCountry(store))
        .map((store) => {
          const country = store.split('-')[0];
          const storeNumber = store.split('-')[1];
          const storeName = storeNameMap.get(store) || '';
          const geo = storeGeoMap.get(store) || '';
          return {
            country,
            geo,
            scopeType: item.scopeType,
            storeConfig: item.value,
            storeName,
            storeNumber,
          };
        }));
  return result;
};

const filterStoresToUpdate = (scopeType, countries, report, storeList) => {
  // Helper to check if a store key is in the specified countries
  const isStoreInCountries = (storeKey) => {
    if (countries.length === 0) return true;
    return countries.some((c) => storeKey.startsWith(c));
  };

  // Helper to convert a store from storeList to output format
  const storeListToOutput = (store, config = 'Undefined', scope = 'Undefined') => ({
    country: store.address.country,
    geo: toGeo(store),
    scopeType: scope,
    storeConfig: config,
    storeName: store.name,
    storeNumber: store.storeNumber,
  });

  // Create maps for store lookup
  const storeNameMap = new Map(
    storeList.map((store) => [
      `${store.address.country}-${store.storeNumber}`,
      store.name,
    ])
  );
  const storeGeoMap = new Map(
    storeList.map((store) => [
      `${store.address.country}-${store.storeNumber}`,
      toGeo(store),
    ])
  );

  // Helper to convert a store from report to output format
  const reportStoreToOutput = (storeKey, reportItem) => {
    const [storeCountry, storeNumber] = storeKey.split('-');
    return {
      country: storeCountry,
      geo: storeGeoMap.get(storeKey) || '',
      scopeType: reportItem.scopeType,
      storeConfig: reportItem.value,
      storeName: storeNameMap.get(storeKey) || '',
      storeNumber,
    };
  };

  // Helper to get stores from report by scope type, filtered by countries
  const getStoresFromReportByScope = (targetScopeType) => {
    const result = [];
    report
      .filter((item) => item.scopeType === targetScopeType)
      .forEach((item) => {
        item.stores
          .filter((store) => isStoreInCountries(store))
          .forEach((store) => {
            result.push(reportStoreToOutput(store, item));
          });
      });
    return result;
  };

  // Helper to get all store keys from report for specified scope types
  const getStoreKeysFromReportByScopes = (scopeTypes) => {
    const keys = new Set();
    report
      .filter((item) => scopeTypes.includes(item.scopeType))
      .forEach((item) => {
        item.stores.forEach((store) => keys.add(store));
      });
    return keys;
  };

  // Helper to filter storeList by countries, excluding certain store keys
  const getStoresFromStoreListExcluding = (excludeKeys) => storeList
      .filter((store) => {
        const storeKey = `${store.address.country}-${store.storeNumber}`;
        return isStoreInCountries(storeKey) && !excludeKeys.has(storeKey);
      })
      .map((store) => storeListToOutput(store));

  // If report is empty
  if (report.length === 0) {
    switch (scopeType) {
      case GLOBAL:
        return storeList.map((store) => storeListToOutput(store));
      case GEO:
      case COUNTRY:
        return storeList
          .filter((store) => {
            const storeKey = `${store.address.country}-${store.storeNumber}`;
            return isStoreInCountries(storeKey);
          })
          .map((store) => storeListToOutput(store));
      case STORE:
        return [storeListToOutput(this.props.currentStore)];
      default:
        return [];
    }
  }

  // If report is not empty
  switch (scopeType) {
    case STORE:
      return [storeListToOutput(this.props.currentStore)];

    case COUNTRY: {
      const storesInCountry = getStoresFromReportByScope(COUNTRY);
      if (storesInCountry.length > 0) {
        return storesInCountry;
      }
      const storeKeysToExclude = getStoreKeysFromReportByScopes([STORE]);
      return getStoresFromStoreListExcluding(storeKeysToExclude);
    }

    case GEO: {
      const storesInGeo = getStoresFromReportByScope(GEO);
      if (storesInGeo.length > 0) {
        return storesInGeo;
      }
      // b. Return stores from storeList in countries, excluding STORE and COUNTRY scope stores from report
      const storeKeysToExclude = getStoreKeysFromReportByScopes([STORE, COUNTRY]);
      return getStoresFromStoreListExcluding(storeKeysToExclude);
    }

    case GLOBAL: {
      // a. Check for GLOBAL scopeType in Report, return stores from report
      const globalStores = getStoresFromReportByScope(GLOBAL);
      if (globalStores.length > 0) {
        return globalStores;
      }
      // b. Return stores from storeList, excluding STORE, COUNTRY, and GEO scope stores from report
      const storeKeysToExclude = getStoreKeysFromReportByScopes([STORE, COUNTRY, GEO]);
      return getStoresFromStoreListExcluding(storeKeysToExclude);
    }

    default:
      return [];
  }
};

const getStoreIdentifier = (store) => {
  if (store?.id) return store.id;
  if (store?.address?.country && store?.storeNumber) {
    return `${store.address.country}-${store.storeNumber}`;
  }
  return 'unknown';
};

class Keyset extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      conflicts: [],
      currentStep: STEP_EDIT,
      isDiffModalLoading: false,
      isModalOpen: false,
      report: [],
      scheduledDate: '',
      scheduledTime: '',
      scheduleForLater: false,
      schedules: [],
      scopeType: 'Undefined',
      selectedRows: new Set(),
      storesToOverridde: [],
      storesToUpdate: [],
      value: '',
    };
  }

  buildBulkUpdateData = () => {
    const bulkData = [];

    // First, add the original save data (current store)
    const originalSaveData = this.buildSaveData();
    if (originalSaveData?.body) {
      const originalData = {
        key: originalSaveData.body.key,
        scopeInfo: [{
          hierarchy: originalSaveData.body.hierarchy,
          scope: originalSaveData.body.scope,
        }],
        scopeType: originalSaveData.body.scopeType,
        value: originalSaveData.body.value,
      };
      bulkData.push(originalData);
    }

    // Group conflicts by scope type
    const selectedConflicts = this.getSelectedConflicts();
    const groupedByScope = selectedConflicts.reduce((acc, item) => {
      const { scopeType } = item;
      if (!acc[scopeType]) {
        acc[scopeType] = [];
      }
      acc[scopeType].push(item);
      return acc;
    }, {});

    // Create data array for each scope type
    Object.entries(groupedByScope).forEach(([scopeType, items]) => {
      const scopeMap = new Map();
      items.forEach((item) => {
        const scope = scopeType === 'STORE'
          ? `${item.country}-${item.storeNumber}`
          : item.country;

        if (!scopeMap.has(scope)) {
          scopeMap.set(scope, {
            hierarchy: {
              brand: this.props.currentStore.brand || 'NIKE',
              country: item.country,
              geo: item.geo,
            },
            scope,
          });
        }
      });

      const scopeInfo = Array.from(scopeMap.values());
      bulkData.push({
        key: this.props.name,
        scopeInfo,
        scopeType,
        value: this.state.value,
      });
    });

    return bulkData;
  };

  buildSaveData = () => {
    const toSend = {
      body: {
        changeRequest: {
          justification: this.generateJustification(),
          requestedBy: this.props.userInfo?.email || '',
        },
        hierarchy: {
          brand: this.props.currentStore.brand,
          country: this.props.currentStore.address.country,
          geo: toGeo(this.props.currentStore),
        },
        key: this.props.name,
        scope: '',
        scopeType: this.state.scopeType,
        value: this.state.value,
      },
      description: this.props.description,
      group: this.props.group,
      keyType: this.props.type?.toUpperCase() || '',
    };
    // set up scope differently depending on scopeType
    switch (this.state.scopeType) {
      case (STORE):
        toSend.body.scope = formatStoreAlias(this.props.currentStore);
        break;
      case (COUNTRY):
        toSend.body.scope = this.props.currentStore.address.country;
        break;
      case (GEO):
        toSend.body.scope = toGeo(this.props.currentStore);
        delete toSend.body.hierarchy;
        break;
      case (GLOBAL):
        toSend.body.scope = GLOBAL;
        delete toSend.body.hierarchy;
        break;
      default:
        break;
    }
    return toSend;
  };

  countStoresCountriesGeos = (data) => data.reduce((acc, item) => {
    const scope = item.scopeType;
    acc[scope] = (acc[scope] || 0) + 1;
    return acc;
  }, {});

  downloadCSV = () => {
    const {
      storesToUpdate, storesToOverridde, scopeType, value
    } = this.state;
    const headers = ['Store Number', 'Store Name', 'Country', 'Geo', 'Current Scope Type', 'Current Value', 'New Scope Type', 'New Value'];

    const escapeCSVField = (field) => {
      const stringField = field?.toString() || '';
      if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
        return `"${stringField.replace(/"/g, '""')}"`;
      }
      return stringField;
    };

    const rows = [
      ...storesToUpdate.map((item) => [
        escapeCSVField(item.storeNumber),
        escapeCSVField(item.storeName),
        escapeCSVField(item.country),
        escapeCSVField(item.geo),
        escapeCSVField(item.scopeType),
        escapeCSVField(item.storeConfig),
        escapeCSVField(scopeType),
        escapeCSVField(value),
      ]),
      ...storesToOverridde.map((item) => [
        escapeCSVField(item.storeNumber),
        escapeCSVField(item.storeName),
        escapeCSVField(item.country),
        escapeCSVField(item.geo),
        escapeCSVField(item.scopeType),
        escapeCSVField(item.storeConfig),
        escapeCSVField(item.scopeType),
        escapeCSVField(value),
      ]),
    ];

    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `SCS_${this.props.name}_Updates.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  fetchCountryList = () => {
    const countryList = [];
    if (this.state.scopeType === GLOBAL) {
      return countryList;
    }
    if (this.state.scopeType === COUNTRY) {
      countryList.push(this.props.currentStore.address.country);
    } else if (this.state.scopeType === GEO) {
      const geo = toGeo(this.props.currentStore);
      if (geo === 'EM') {
        countryList.push(this.props.currentStore.address.country);
      } else {
        const countries = getCountriesByGeo(geo);
        countries.forEach((country) => {
          if (country && country !== 'Undefined') {
            countryList.push(country);
          }
        });
      }
    }
    return countryList;
  };

  generateJustification = () => `${this.props.userInfo?.email || 'User'} has updated the ${this.props.name} to ${this.state.value}`;

  getBooleanValueString = () => (
    typeof this.state.value === 'boolean' ? this.state.value.toString() : this.state.value
  );

  getScheduleDisabledReason = () => {
    const { scopeType, schedules } = this.state;
    const hasPendingSchedule = schedules.some((schedule) => (
      schedule.key === this.props.name
      && schedule.status === 'PENDING'
    ));

    if (scopeType === 'Undefined') {
      return ` `;
    }

    if (hasPendingSchedule) {
      return `Schedule pending for ${this.props.name} key`;
    }

    return null; // Not disabled
  };

  getSelectedConflicts = () => this.state.conflicts.filter((_, i) => this.state.selectedRows.has(i));

  handleDiffCancel = () => {
    trackScsScopeDiffCheckCancelled({
      conflictsCount: this.state.conflicts.length,
      keyName: this.props.name,
    });
    this.onCloseModal();
  };

  handleDiffProceed = () => {
    const selectedConflicts = this.getSelectedConflicts();
    this.setState({ currentStep: STEP_CONFIRM, storesToOverridde: selectedConflicts });
  };

  handleDiffRowSelect = (rowIndex, isChecked) => {
    this.setState((prevState) => {
      const newSelected = new Set(prevState.selectedRows);
      if (isChecked) {
        newSelected.add(rowIndex);
      } else {
        newSelected.delete(rowIndex);
      }
      return { selectedRows: newSelected };
    });
  };

  handleDiffSelectAll = (isChecked, totalRows) => {
    if (isChecked) {
      const allIndices = Array.from({ length: totalRows }, (_, i) => i);
      this.setState({ selectedRows: new Set(allIndices) });
    } else {
      this.setState({ selectedRows: new Set() });
    }
  };

  isUnchanged = () => {
    const currentScopeType = this.props.currentPair.scopeType || 'Undefined';
    const currentValue = this.props.currentPair.value;
    const newScopeType = this.state.scopeType;
    const newValue = this.state.value;

    // Compare scope types
    if (currentScopeType !== newScopeType) return false;

    // Compare values (handle type coercion for booleans/numbers)
    return String(currentValue) === String(newValue);
  };

  loadInitialScsKeyData = async () => {
    this.setState({ isDiffModalLoading: true });
    const [reportResult, scheduleResult] = await Promise.allSettled([
      scsReportGet(this.props.accessToken, this.props.name, this.props.scsRegion),
      scsScheduleGet(this.props.accessToken, this.props.scsRegion, 'PENDING'),
    ]);

    const report = reportResult.status === 'fulfilled' ? reportResult.value?.body || [] : [];
    const schedules = scheduleResult.status === 'fulfilled' ? scheduleResult.value?.body?.schedules || [] : [];

    this.setState({
      isDiffModalLoading: false,
      report,
      schedules,
    });
  };

  onCloseModal = () => {
    this.setState({
      conflicts: [],
      currentStep: STEP_EDIT,
      isDiffModalLoading: false,
      isModalOpen: false,
      report: [],
      scheduledDate: '',
      scheduledTime: '',
      scheduleForLater: false,
      schedules: [],
      scopeType: 'Undefined',
      selectedRows: new Set(),
      storesToOverridde: [],
      storesToUpdate: [],
      value: '',
    });
  };

  onInputChange = (value, prop = 'value') => {
    const newValue = value.target && (value.target.value || value.target.value === '')
      ? value.target.value
      : value;

    // Track scope change
    if (prop === 'scopeType' && newValue !== this.state.scopeType) {
      trackScsKeyScopeChanged({
        keyName: this.props.name,
        newScope: newValue,
        oldScope: this.state.scopeType,
      });
    }

    this.setState({ [prop]: newValue });
  };

  onNext = async () => {
    const { value: newValue, scopeType: newScopeType } = this.state;

    // STORE scope: go directly to confirm
    if (newScopeType === STORE) {
      const storesFilteredForUpdate = [{
        country: this.props.currentStore.address?.country,
        geo: toGeo(this.props.currentStore),
        scopeType: this.props.currentPair.scopeType,
        storeConfig: this.props.currentPair.value,
        storeName: this.props.currentStore.name,
        storeNumber: this.props.currentStore.storeNumber,
      }];
      this.setState({ currentStep: STEP_CONFIRM, storesToUpdate: storesFilteredForUpdate });
      return;
    }

    // For non-STORE scopes, fetch report and check for conflicts
    const { report } = this.state;
    const countries = this.fetchCountryList();
    const storesFilteredForUpdate = filterStoresToUpdate(
        newScopeType, countries, report, this.props.allStores
      );

      // Only check for conflicts on COUNTRY scope
      let conflicts = [];
      if (newScopeType === COUNTRY) {
        conflicts = filterStoresWithDiffVal(newValue, newScopeType, countries, report, this.props.allStores);
        if (conflicts.length > 0) {
          trackScsScopeDiffCheckTriggered({
            conflictsCount: conflicts.length,
            keyName: this.props.name,
            scopeType: newScopeType,
          });
          this.setState({
            conflicts, currentStep: STEP_DIFF_CHECK, storesToUpdate: storesFilteredForUpdate
          });
          return;
        }
      }
      this.setState({
        conflicts: [], currentStep: STEP_CONFIRM, storesToUpdate: storesFilteredForUpdate
      });
  };

  onOpenModal = () => {
    // Track when user starts editing a key
    trackScsKeyEditStarted({
      currentScope: this.props.currentPair.scopeType,
      currentValue: this.props.currentPair.value,
      group: this.props.group,
      keyName: this.props.name,
      storeId: getStoreIdentifier(this.props.currentStore),
    });
    this.loadInitialScsKeyData();
    this.setState({ isModalOpen: true });
  };

  onSave = async () => {
    if (this.saveDisabled()) {
      return;
    }
    const totalCount = this.state.storesToOverridde.length + this.state.storesToUpdate.length;

    // If there are selected conflicts, use bulk update
    const selectedConflicts = this.getSelectedConflicts();
    if (selectedConflicts.length > 0) {
      const bulkData = this.buildBulkUpdateData();
      const body = {
        changeRequest: {
          justification: this.generateJustification(),
          requestedBy: this.props.userInfo?.email,
        },
        data: bulkData,
      };
      this.onCloseModal();
      this.props.onBulkSave({
        body,
        keyName: this.props.name,
        selectedCount: selectedConflicts.length,
        totalCount
      });
    } else {
      const toSend = this.buildSaveData();
      toSend.totalCount = totalCount;
      this.onCloseModal();
      this.props.onSave(toSend);
    }
  };

  onSchedule = () => {
    const schedules = [];

    // First, add the original save data (current store) as a schedule
    const originalSaveData = this.buildSaveData();
    if (originalSaveData?.body) {
      const originalSchedule = {
        hierarchy: originalSaveData.body.hierarchy || {
          brand: this.props.currentStore.brand || 'NIKE',
          country: this.props.currentStore.address.country,
          geo: toGeo(this.props.currentStore),
        },
        key: originalSaveData.body.key,
        scope: originalSaveData.body.scope,
        scopeType: originalSaveData.body.scopeType,
        value: originalSaveData.body.value,
      };
      schedules.push(originalSchedule);
    }

    // Add selected conflicts as schedules
    const selectedConflicts = this.getSelectedConflicts();
    const seenStoreScopes = new Set();
    selectedConflicts.forEach((item) => {
      const scope = item.scopeType === 'STORE'
        ? `${item.country}-${item.storeNumber}`
        : item.country;

      if (item.scopeType === STORE) {
        if (seenStoreScopes.has(scope)) {
          return;
        }
        seenStoreScopes.add(scope);
      }

      schedules.push({
        hierarchy: {
          brand: this.props.currentStore.brand || 'NIKE',
          country: item.country,
          geo: item.geo,
        },
        key: this.props.name,
        scope,
        scopeType: item.scopeType,
        value: this.state.value,
      });
    });

    // Combine scheduledDate and scheduledTime to create ISO format
    const scheduledStartTime = this.state.scheduledDate && this.state.scheduledTime
      ? `${this.state.scheduledDate}T${this.state.scheduledTime}:00`
      : '';

    const body = {
      changeRequest: {
        createdBy: this.props.userInfo?.email,
        justification: this.generateJustification(),
      },
      scheduledStartTime,
      schedules,
    };

    // Call the parent's onSchedule function with the payload
    this.onCloseModal();
    this.props.onSchedule({
        body,
        keyName: this.props.name,
      });
  };

  saveDisabled = () => {
    const baseValidation = this.state.value === 'Undefined' || this.state.scopeType === 'Undefined';

    if (this.state.scheduleForLater) {
      return baseValidation || !this.state.scheduledDate || !this.state.scheduledTime;
    }

    return baseValidation;
  };

  renderDiffCheckStep = () => {
    const { conflicts, selectedRows } = this.state;
    const totalRows = conflicts.length;
    const isAllSelected = totalRows > 0 && selectedRows.size === totalRows;

    const scopeCounts = this.countStoresCountriesGeos(conflicts);
    const scopeLevels = [
      { display: 'STORE Level', label: 'STORE' },
      { display: 'COUNTRY Level', label: 'COUNTRY' },
      { display: 'GEO Level', label: 'GEO' },
    ];
    const scopeDiffString = scopeLevels
      .filter(({ label }) => scopeCounts[label] > 0)
      .map(
        ({ label, display }) => `${scopeCounts[label]} stores at the ${display}`
      )
      .join(', ');

    const columns = [
      {
        accessor: () => '',
        Cell: (rowInfo) => (
          <div style={styles.checkboxCellContainer}>
            <input
              checked={selectedRows.has(rowInfo.index)}
              id={`scope-diff-checkbox-${rowInfo.index}`}
              style={styles.checkbox}
              type="checkbox"
              onChange={(e) => {
                e.stopPropagation();
                this.handleDiffRowSelect(rowInfo.index, e.target.checked);
              }}
            />
          </div>
        ),
        Header: () => (
          <button
            style={styles.selectAllButton}
            type="button"
            onClick={() => this.handleDiffSelectAll(!isAllSelected, totalRows)}
          >
            {isAllSelected ? 'Deselect All' : 'Select All'}
          </button>
        ),
        id: 'select',
        sortable: false,
        width: 120,
      },
      {
        accessor: (data) => data.storeNumber,
        Header: 'Store #',
        id: 'Store #',
      },
      {
        accessor: (data) => data.storeName,
        Header: 'Store Name',
        id: 'Store Name',
      },
      {
        accessor: (data) => data.country,
        Header: 'Country',
        id: 'Country',
      },
      {
        accessor: (data) => data.storeConfig,
        Header: 'Value',
        id: 'Store Config Value',
      },
      {
        accessor: (data) => data.scopeType,
        Header: 'Scope',
        id: 'Store Scope',
      },
    ];

    return (
      <>
        <br />
        <p className="headline-5 ta-sm-c">
          {`Value differences detected for config ${this.props.name}${
            scopeDiffString ? ` for ${scopeDiffString}` : ''
          }`}
        </p>
        <br />
        <TableRowClick
          showPagination
          columns={columns}
          data={conflicts}
          defaultPageSize={5}
          handleClick={() => {}}
        />
        <br />
        <br />
        <p className="headline-5 ta-sm-c text-color-accent">
          Warning: All the Stores Selected will be Updated.
        </p>
        <article className="ncss-col-sm-12">
          <div style={styles.buttonGroup}>
            <ButtonWhite
              label="Back"
              onClick={() => this.setState({
                conflicts: [], currentStep: STEP_EDIT, selectedRows: new Set(), storesToOverridde: [], storesToUpdate: []
            })}
            />
            <ButtonBlack
              label="Next"
              onClick={this.handleDiffProceed}
            />
          </div>
        </article>
      </>
    );
  };

  renderConfirmationStep = () => {
    const {
      scopeType,
      value,
      storesToUpdate,
      storesToOverridde,
    } = this.state;
    const totalStores = storesToUpdate.length + storesToOverridde.length;

    // Get the scope display value based on scope type
    const getScopeDisplayValue = () => {
      switch (scopeType) {
        case STORE:
          return `${this.props.currentStore.storeNumber} ${this.props.currentStore.name}`;
        case COUNTRY:
          return this.props.currentStore.address?.country;
        case GEO:
          return toGeo(this.props.currentStore);
        case GLOBAL:
          return 'All Stores';
        default:
          return '';
      }
    };

    return (
      <>
        <section className="ncss-row">
          <p className="ncss-col-sm-12 headline-5 ta-sm-c mb4-sm">Please review your changes before confirming:</p>
        </section>
        <section className="ncss-row">
          <article className="ncss-col-sm-12 ncss-col-md-6 va-sm-t mb2-sm">
            <p className="body-2 text-color-secondary">Key Name</p>
            <p className="body-2">{this.props.name}</p>
          </article>
          <article className="ncss-col-sm-12 ncss-col-md-6 va-sm-t mb2-sm">
            <p className="body-2 text-color-secondary">Applied at</p>
            <p className="body-2 text-color-accent">
              {scopeType} ({getScopeDisplayValue()})
            </p>
          </article>
        </section>
        <section className="ncss-row">
          <article className="ncss-col-sm-12 va-sm-t mb2-sm">
            <p className="body-2 text-color-secondary">Value</p>
            <p className="body-2 text-color-accent">{value?.toString()}</p>
          </article>
        </section>
        {this.state.scheduleForLater && (
          <section className="ncss-row">
            <article className="ncss-col-sm-12 va-sm-t mb2-sm">
              <ScheduleBanner
                scheduledDate={this.state.scheduledDate}
                scheduledTime={this.state.scheduledTime}
                variant="confirmation"
              />
            </article>
          </section>
        )}
        <section className="ncss-row">
          <article className="ncss-col-sm-12 va-sm-t mb2-sm">
            <div style={styles.greySection}>
              <p className="body-2" style={styles.boldText}>
                Effective for {totalStores} stores
              </p>
              <p className="body-2" style={styles.boldText}>Impact Summary:</p>
              <p className="body-2">
                Stores receiving a value update via {scopeType.toLowerCase()} scope → {storesToUpdate.length}
              </p>
              <p className="body-2">
                Stores receiving a value override (scope unchanged) → {storesToOverridde.length}
              </p>
              {this.state.scheduleForLater && (
                <p className="body-2" style={{ marginTop: '12px' }}>
                  <span style={{ fontWeight: 'bold' }}>This change will NOT apply immediately.</span> It will be queued and applied automatically at the scheduled time.
                </p>
              )}
            </div>
          </article>
        </section>
        <section className="ncss-row mt4-sm" style={styles.buttonGroupWithDownload}>
          <div style={styles.downloadButtonContainer}>
            <ButtonBlack
              label="Download Updates"
              onClick={this.downloadCSV}
            />
          </div>
          <ButtonWhite
            label="Back"
            onClick={() => this.setState((prevState) => ({
                conflicts: prevState.conflicts.length > 0 ? prevState.conflicts : [],
                currentStep: prevState.conflicts.length > 0 ? STEP_DIFF_CHECK : STEP_EDIT,
                selectedRows: prevState.conflicts.length > 0 ? prevState.selectedRows : new Set(),
                storesToOverridde: prevState.conflicts.length > 0 ? prevState.storesToOverridde : [],
                storesToUpdate: prevState.conflicts.length > 0 ? prevState.storesToUpdate : [],
              }))}
          />
          <ButtonBlack
            label={this.state.scheduleForLater ? 'Schedule' : 'Confirm'}
            onClick={this.state.scheduleForLater ? this.onSchedule : this.onSave}
          />
        </section>
      </>
    );
  };

  renderEditStep = () => (
    <>
      <section className="ncss-row mt4-sm">
        <article className="ncss-col-sm-12 ncss-col-md-6 va-sm-t mb2-sm">
          <p className="body-2 text-color-secondary">Key Name</p>
          <p className="body-2">{this.props.name}</p>
        </article>
        {this.props.description && (
          <article className="ncss-col-sm-12 ncss-col-md-6 va-sm-t mb2-sm">
            <p className="body-2 text-color-secondary">Key Description</p>
            <p className="body-2 text-color-primary-dark">{this.props.description}</p>
          </article>
        )}
      </section>
      {/* Current Values - Read Only */}
      <section className="ncss-row mt2-sm">
        <article className="ncss-col-sm-12 ncss-col-md-6 va-sm-t mb2-sm">
          <p className="body-2 text-color-secondary">Current Scope Type</p>
          <p className="body-2">{this.props.currentPair.scopeType || 'Undefined'}</p>
        </article>
        <article className="ncss-col-sm-12 ncss-col-md-6 va-sm-t mb2-sm">
          <p className="body-2 text-color-secondary">Current Value</p>
          <p className="body-2">{this.props.currentPair.value?.toString() || 'Undefined'}</p>
        </article>
      </section>
      {/* Tip Section */}
      <section className="ncss-row mt2-sm">
        <article className="ncss-col-sm-12">
          <p className="body-2 text-color-secondary" style={styles.tipBox}>
            <strong>Tip:</strong> Make sure you select the appropriate scope before entering a new value
            <br />
            <span>
              STORE = Only this store ({this.props.currentStore.name || this.props.currentStore.storeNumber}) |
              COUNTRY = {this.props.currentStore.address?.country} |
              GEO = {toGeo(this.props.currentStore)} |
              GLOBAL = All Stores
            </span>
          </p>
        </article>
      </section>
      {/* New Values - Editable */}
      <section className="ncss-row mt2-sm">
        <article className="ncss-col-sm-12 ncss-col-md-6 va-sm-t" style={styles.articleMinHeight}>
          <Select
            errorMessage={this.state.scopeType === 'Undefined' ? REQUIRED_FIELD : ''}
            id="scopeType"
            label="New Scope Type"
            options={[
              { label: 'Select', value: 'Undefined' },
              ...[GEO, COUNTRY, STORE, GLOBAL].map((value) => ({ label: value, value })),
            ]}
            value={this.state.scopeType}
            onChange={(value) => this.onInputChange(value, 'scopeType')}
          />
        </article>
        {this.props.type === 'boolean'
          ? (
            <article className="ncss-col-sm-12 ncss-col-md-6 va-sm-t" style={styles.articleMinHeight}>
              <Select
                errorMessage={this.state.value === 'Undefined' || this.state.value === '' ? REQUIRED_FIELD : ''}
                id="value"
                label="New Value"
                options={[
                  { label: 'Select', value: '' },
                  { label: 'true', value: 'true' },
                  { label: 'false', value: 'false' }
                ]}
                value={this.getBooleanValueString()}
                onChange={this.onInputChange}
              />
            </article>
          )
          : (
            <article className="ncss-col-sm-12 ncss-col-md-6 va-sm-t" style={styles.articleMinHeight}>
              <Input
                errorMessage={!this.state.value || !this.state.value.toString().trim() ? REQUIRED_FIELD : ''}
                label="New Value"
                style={styles.inputWithZIndex}
                type={(this.props.type === 'integer') ? 'number' : 'text'}
                value={this.state.value === 'Undefined' ? '' : this.state.value}
                onChange={this.onInputChange}
              />
            </article>
          )}
      </section>
      <section className="ncss-row">
        <ScheduleForLater
          disabled={!!this.getScheduleDisabledReason()}
          disabledReason={this.getScheduleDisabledReason()}
          id="scheduleForLaterToggle"
          scheduleForLater={this.state.scheduleForLater}
          scheduledDate={this.state.scheduledDate}
          scheduledTime={this.state.scheduledTime}
          onDateChange={(value) => this.setState({ scheduledDate: value })}
          onTimeChange={(value) => this.setState({ scheduledTime: value })}
          onToggle={(checked) => this.setState({ scheduleForLater: checked })}
        />
      </section>
      <section className="ncss-row" style={styles.buttonGroup}>
        <ButtonWhite label="Cancel" onClick={this.onCloseModal} />
        <ButtonBlack
          isDisabled={this.saveDisabled() || this.state.isDiffModalLoading || this.isUnchanged()}
          label="Next"
          onClick={this.onNext}
        />
      </section>
    </>
  );

  render = () => (
    <article className="border bg-white mb2-sm p2-sm ta-sm-l">
      <div className="mb2-sm u-cursor-pointer" role="menuitem" tabIndex={0}>
        <span>{this.props.name} | {this.props.currentPair.value.toString().replace(/,/g, ', ').replace(/websurvey/g, 'websurvey ')}</span>
        <ButtonBlack className="fl-sm-r" isDisabled={this.props.isReadOnly} label="Edit" onClick={this.onOpenModal} />
      </div>
      {/* Edit Modal - Step 1 */}
      <Modal
        isOpen={this.state.isModalOpen && this.state.currentStep === STEP_EDIT}
        loading={this.state.isDiffModalLoading}
        title={`Edit config for: ${this.props.currentStore.storeNumber} ${this.props.currentStore.name || ''} (${this.props.currentStore.address?.country}, ${toGeo(this.props.currentStore)})`}
        onClose={this.onCloseModal}
      >
        {this.renderEditStep()}
      </Modal>
      {/* Confirmation Modal - Step 2 (for non-COUNTRY) or Step 3 (after diff check) */}
      <Modal
        isOpen={this.state.isModalOpen && this.state.currentStep === STEP_CONFIRM}
        title="Confirm Changes"
        onClose={this.onCloseModal}
      >
        {this.renderConfirmationStep()}
      </Modal>
      {/* Scope Diff Check Modal - Step 2 (for COUNTRY with conflicts) */}
      <Modal
        isOpen={this.state.isModalOpen && this.state.currentStep === STEP_DIFF_CHECK}
        title="Config Value Mismatch Detected"
        onClose={this.handleDiffCancel}
      >
        {this.renderDiffCheckStep()}
      </Modal>
    </article>
  );
}

Keyset.defaultProps = {
  currentPair: {
    scopeType: 'Undefined',
    value: 'Undefined',
  },
};

Keyset.propTypes = {
  accessToken: PropTypes.string.isRequired,
  allStores: PropTypes.arrayOf(PropTypes.shape).isRequired,
  currentPair: PropTypes.shape({
    scopeType: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.number]),
  }),
  currentStore: PropTypes.shape({
    address: PropTypes.shape({
      country: PropTypes.string,
    }),
    brand: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
    storeNumber: PropTypes.string,
  }).isRequired,
  description: PropTypes.string.isRequired,
  group: PropTypes.string.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  onBulkSave: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onSchedule: PropTypes.func.isRequired,
  scsRegion: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  userInfo: PropTypes.shape({
    email: PropTypes.string,
  }).isRequired,
};

export default Keyset;
